import {
  animate,
  state,
  style,
  transition,
  trigger,
} from '@angular/animations';
import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import { CdkPortalOutlet } from '@angular/cdk/portal';
import { DOCUMENT } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  Injector,
  OnDestroy,
  OnInit,
  TemplateRef,
  ViewChild,
  createNgModule,
  signal,
  inject,
} from '@angular/core';
import { MatSidenav } from '@angular/material/sidenav';
import { Title } from '@angular/platform-browser';
import { NavigationEnd, Router } from '@angular/router';
import { toObservable } from '@angular/core/rxjs-interop';
import { Actions, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { LazyDialogLoader } from '@qtek/libs/dialog-loader';
import {
  HealthcheckCoreService,
  QtNetError,
} from '@qtek/libs/healthcheck-core';
import {
  AppState,
  AuthActions,
  CoreStore,
  UserStore,
  WizardActions,
  selectMaxCompanyProgress,
  selectMaxPersonProgress,
} from '@qtek/libs/store';
import { PermissionsService, PermissionType } from '@qtek/libs/permissions';
import {
  DynamicDrawer,
  OnlineService,
  RIGHT_SIDENAV_STATE,
  UiTourService,
} from '@qtek/shared/services';
import {
  IMPERSONATION_EMAIL_KEY,
  IMPERSONATION_TOKEN_KEY,
  resolveImpersonationContext,
  setImpersonationDomState,
} from '@qtek/shared/utils';
import { Observable, Subject, combineLatest, from, of, timer } from 'rxjs';
import {
  debounceTime,
  distinctUntilChanged,
  filter,
  map,
  mergeMap,
  shareReplay,
  startWith,
  switchMap,
  take,
  takeUntil,
  tap,
} from 'rxjs/operators';
import { RightSidenavStateService } from '../../services/right-sidenav-state.service';
import { RightSidenavComponent } from '../right-sidenav/right-sidenav.component';

const EXPAND_ANIMATION = trigger('size', [
  state(
    '1',
    style({
      width: '220px',
    })
  ),
  state(
    '0',
    style({
      width: '70px',
    })
  ),
  state(
    'hidden',
    style({
      width: '0px',
      display: 'none',
    })
  ),
  transition('1 <=> 0', [animate('220ms cubic-bezier(0.4, 0, 0.2, 1)')]),
  transition('* <=> hidden', [
    animate('200ms cubic-bezier(0.25, 0.8, 0.25, 1)'),
  ]),
]);

export const sideNavContainerAnimation = trigger('openCloseSidenavContent', [
  state(
    '1',
    style({
      'margin-left': '220px',
    })
  ),
  state(
    '0',
    style({
      'margin-left': '70px',
    })
  ),
  state(
    'mobile',
    style({
      'margin-left': '0',
    })
  ),
  state(
    'hidden',
    style({
      'margin-left': '0',
    })
  ),
  transition('1 <=> 0', [animate('220ms cubic-bezier(0.4, 0, 0.2, 1)')]),
  transition('* <=> hidden', [
    animate('200ms cubic-bezier(0.25, 0.8, 0.25, 1)'),
  ]),
]);

@Component({
  selector: 'qt-layout',
  templateUrl: 'layout.component.html',
  styleUrls: ['./layout.component.scss'],
  /** Provide service directly to destroy with component */
  providers: [
    UiTourService,
    RightSidenavStateService,
    {
      provide: RIGHT_SIDENAV_STATE,
      useExisting: RightSidenavStateService,
    },
  ],
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [EXPAND_ANIMATION, sideNavContainerAnimation],
})
export class LayoutComponent implements OnInit, AfterViewInit, OnDestroy {
  private document = inject<Document>(DOCUMENT);
  router = inject(Router);
  uiTourService = inject(UiTourService);
  private actions$ = inject(Actions);
  private injector = inject(Injector);
  private titleService = inject(Title);
  private online = inject(OnlineService);
  private store = inject<Store<AppState>>(Store);
  private coreStore = inject(CoreStore);
  private userStore = inject(UserStore);
  private dynamicDrawer = inject(DynamicDrawer);
  private observer = inject(BreakpointObserver);
  private healthCheck = inject(HealthcheckCoreService);
  private rightSidenavState = inject(RightSidenavStateService);
  private dialogLoader = inject(LazyDialogLoader);
  private permissions = inject(PermissionsService);

  actualStepNumber: number;
  maxStepNumber: number;
  isUiTourOpen: boolean;
  mini$: Observable<boolean>;
  wizardOpened: boolean;
  destroy$ = new Subject<void>();
  networkError$: Observable<QtNetError | null> = of(null);
  mode$: Observable<'side' | 'over'>;
  mobile$: Observable<boolean>;
  isUploadRoute$: Observable<boolean>;
  readonly rightSidenavWidth = signal<number>(350);
  /** Tracks right panel open state so margin-right updates reliably with OnPush when panel closes. */
  readonly rightPanelOpened = signal<boolean>(false);
  rightSidenavActiveTab: 'chat' | 'tasks' | null = null;
  canShowAiChat = false;
  canAddDeal = false;
  readonly isImpersonating = signal(false);
  impersonatedEmail$: Observable<string>;

  isLargeScreen$: Observable<boolean>;

  readonly SIDENAV_W = { expanded: 220, mini: 70 };

  /** Tracks left sidebar expanded (true) vs minimized (false) so it stays minimized with OnPush. */
  readonly leftSidenavExpanded = signal<boolean>(true);

  @ViewChild('childDiv', { static: true }) childDiv: ElementRef;
  @ViewChild('dynamicSidenav') dynamicSidenav: MatSidenav;
  @ViewChild(CdkPortalOutlet) outlet: CdkPortalOutlet;
  @ViewChild('tutorialTemplateRef') tutorialTemplateRef: TemplateRef<any>;
  @ViewChild('sidenav') sidenav: MatSidenav;
  @ViewChild('rightSidenav') rightSidenav: MatSidenav;
  @ViewChild(RightSidenavComponent)
  rightSidenavComponent: RightSidenavComponent;

  ngOnInit() {
    const userState$ = combineLatest({
      prs: toObservable(this.userStore.currentPerson, {
        injector: this.injector,
      }),
      acn: toObservable(this.userStore.mainCompany, {
        injector: this.injector,
      }),
      rel: toObservable(this.userStore.userRelation, {
        injector: this.injector,
      }),
    });
    const impersonationContext$ = userState$.pipe(
      map(userState =>
        resolveImpersonationContext(
          userState,
          sessionStorage.getItem(IMPERSONATION_TOKEN_KEY),
          sessionStorage.getItem(IMPERSONATION_EMAIL_KEY)
        )
      ),
      tap(({ active, email }) => {
        if (active && email) {
          sessionStorage.setItem(IMPERSONATION_EMAIL_KEY, email);
        }
      }),
      shareReplay(1)
    );

    this.impersonatedEmail$ = impersonationContext$.pipe(
      map(({ email }) => email),
      distinctUntilChanged()
    );

    impersonationContext$
      .pipe(
        map(({ active }) => active),
        distinctUntilChanged(),
        takeUntil(this.destroy$)
      )
      .subscribe(isActive => this.isImpersonating.set(isActive));

    this.isUploadRoute$ = this.router.events.pipe(
      filter(event => event instanceof NavigationEnd),
      map(() => this.router.url.includes('/upload')),
      startWith(this.router.url.includes('/upload')),
      distinctUntilChanged(),
      shareReplay(1)
    );

    this.mobile$ = this.observer.observe(Breakpoints.XSmall).pipe(
      map(breakpoint => Boolean(breakpoint.matches)),
      distinctUntilChanged(),
      shareReplay()
    );

    this.isLargeScreen$ = this.observer
      .observe([Breakpoints.Large, Breakpoints.XLarge])
      .pipe(
        map(breakpoint => Boolean(breakpoint.matches)),
        distinctUntilChanged(),
        shareReplay()
      );

    this.mini$ = this.mobile$.pipe(
      map(isMobile => !isMobile),
      shareReplay(1)
    );

    this.startWizard();

    this.permissions
      .isAllowed$('invitations', PermissionType.Add)
      .pipe(takeUntil(this.destroy$))
      .subscribe(isAllowed => (this.canAddDeal = isAllowed));

    /** Update title to company name. */
    toObservable(this.userStore.mainCompany, { injector: this.injector })
      .pipe(takeUntil(this.destroy$))
      .subscribe(company => {
        if (company?.name?.name) {
          this.titleService.setTitle(company.name.name);
        }
      });

    this.uiTourService.refreshObservable
      .pipe(takeUntil(this.destroy$), debounceTime(0))
      .subscribe(() => {
        this.actualStepNumber = this.uiTourService.uiStepIndex + 1;
        this.maxStepNumber = this.uiTourService.visibleSteps.length;
        this.isUiTourOpen =
          this.uiTourService.isOpen && this.uiTourService.wizardDone;
      });

    const onlineStatus$ = this.online.change$.pipe(
      startWith(this.online.isOnline()),
      shareReplay()
    );

    const httpHealthCheck$ = this.healthCheck.isHealthyHttpConnection$.pipe(
      distinctUntilChanged(),
      switchMap(value => {
        if (value) {
          return of(value);
        }
        return timer(2000).pipe(map(() => value));
      })
    );

    const websocketHealthCheck$ = this.healthCheck.isWebSocketAlive$.pipe(
      distinctUntilChanged(),
      switchMap(value => {
        if (value) {
          return of(value);
        }
        return timer(5000).pipe(map(() => value));
      })
    );

    this.networkError$ = combineLatest([
      onlineStatus$,
      httpHealthCheck$,
      websocketHealthCheck$,
    ]).pipe(
      map(([isOnline, isHttpHealthy, isWsHealthy]) => {
        if (!isOnline) {
          return 'offline';
        }
        if (!isHttpHealthy) {
          return 'http';
        }
        if (!isWsHealthy) {
          return 'ws';
        }
        return null;
      }),
      distinctUntilChanged()
    );

    this.mode$ = this.mini$.pipe(map(bool => (bool ? 'side' : 'over')));
  }

  ngAfterViewInit() {
    this.dynamicDrawer.register({
      matDrawer: this.dynamicSidenav,
      cdkPortalOutlet: this.outlet,
    });

    this.uiTourService.templateRef = this.tutorialTemplateRef;

    const opened = this.rightSidenav?.opened ?? false;
    this.rightPanelOpened.set(opened);
    this.rightSidenavState.setOpened(opened);
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  next(): void {
    this.uiTourService.next();
  }

  back(): void {
    this.uiTourService.back();
  }

  finishUiTour() {
    this.uiTourService.isTourOpen = false;
    this.userStore
      .updateCurrentUser(
        {},
        { _attrv: 'usr', _uitut: this.uiTourService.progress }
      )
      .subscribe();
  }

  toggleLeftSidenav(): void {
    this.leftSidenavExpanded.update(v => !v);
  }

  toggleRightSidenav() {
    if (this.rightSidenav) {
      const next = !this.rightSidenav.opened;
      this.rightPanelOpened.set(next);
      this.rightSidenavState.setOpened(next);
      this.rightSidenav.toggle().then(() => {
        const opened = this.rightSidenav.opened;
        this.rightPanelOpened.set(opened);
        this.rightSidenavState.setOpened(opened);
        if (opened) {
          const component = this.rightSidenavComponent;
          this.rightSidenavActiveTab = component
            ? component.selectedTab()
            : null;
        } else {
          this.rightSidenavActiveTab = null;
        }
      });
    }
  }

  toggleRightSidenavTab(tab: 'chat' | 'tasks') {
    if (this.rightSidenav?.opened && this.rightSidenavActiveTab === tab) {
      this.rightPanelOpened.set(false);
      this.rightSidenavState.setOpened(false);
      this.rightSidenav.close();
      this.rightSidenavActiveTab = null;
      return;
    }
    this.openRightSidenavTab(tab);
  }

  openRightSidenavTab(tab: 'chat' | 'tasks'): boolean {
    const component = this.rightSidenavComponent;
    if (!component) {
      return false;
    }
    const canOpen = component.openTab(tab);
    if (!canOpen) {
      if (tab === 'chat') {
        return this.openRightSidenavTab('tasks');
      }
      return false;
    }
    this.rightSidenavActiveTab = tab;
    if (this.rightSidenav && !this.rightSidenav.opened) {
      this.rightPanelOpened.set(true);
      this.rightSidenavState.setOpened(true);
      this.rightSidenav.open();
    }
    return true;
  }

  onRightSidenavTabChange(tab: 'chat' | 'tasks') {
    this.rightSidenavActiveTab = tab;
  }

  onChatAvailabilityChange(canShow: boolean) {
    this.canShowAiChat = canShow;
    if (!canShow && this.rightSidenavActiveTab === 'chat') {
      this.rightSidenavActiveTab = null;
    }
  }

  onRightSidenavWidthChange(width: number) {
    this.rightSidenavWidth.set(width);
  }

  addNoteFromToolbar() {
    this.rightSidenavComponent?.addNote();
  }

  addContactFromToolbar() {
    this.rightSidenavComponent?.addContact();
  }

  addDealFromToolbar() {
    this.dialogLoader.open('select-deal-type').subscribe();
  }

  endImpersonationSession(): void {
    sessionStorage.removeItem(IMPERSONATION_TOKEN_KEY);
    sessionStorage.removeItem(IMPERSONATION_EMAIL_KEY);
    setImpersonationDomState(this.document, false);
    this.isImpersonating.set(false);
    this.store.dispatch(AuthActions.logout({}));
  }

  /**
   * Load wizard module and display dialog.
   */
  private openWizard() {
    return from(import('@qtek/libs/wizard')).pipe(
      mergeMap(({ WizardModule: WizardModule }) => {
        const moduleRef = createNgModule(WizardModule, this.injector);
        this.wizardOpened = true;
        return moduleRef.instance.openWizard().pipe(
          tap(() => {
            this.wizardOpened = false;
          })
        );
      })
    );
  }

  private startWizard() {
    /** Load wizard steps */
    this.store.dispatch(WizardActions.getSteps());

    const isWizardDone$ = this.getIsWizardDone();

    const success$ = this.actions$.pipe(
      ofType(WizardActions.getStepsSuccess),
      take(1)
    );
    success$
      .pipe(
        mergeMap(() => isWizardDone$),
        take(1),
        map(wizardDone => Boolean(wizardDone)),
        mergeMap((done: boolean) => {
          this.coreStore.closeLoading();
          if (!done) {
            return this.openWizard();
          } else {
            return of(null);
          }
        }),
        mergeMap(() => {
          this.uiTourService.wizardDone = true;
          this.uiTourService.canOpenTour = this.uiTourService.canOpenTourValue;

          return toObservable(this.userStore.userActions, {
            injector: this.injector,
          });
        }),
        tap(() => this.store.dispatch(WizardActions.isWizardDone())),
        filter(value => value && !this.wizardOpened),
        take(1),
        filter(res => res[0] != null && res[0].params != null),
        map(res => res[0].params),
        takeUntil(this.destroy$)
      )
      .subscribe();
  }

  private getIsWizardDone(): Observable<boolean> {
    const personProgress$ = toObservable(this.userStore.personProgress, {
      injector: this.injector,
    });
    const maxPersonProgress$ = this.store.select(selectMaxPersonProgress);
    const companyProgress$ = toObservable(this.userStore.companyProgress, {
      injector: this.injector,
    });
    const maxCompanyProgress$ = this.store.select(selectMaxCompanyProgress);

    return combineLatest([
      personProgress$,
      maxPersonProgress$,
      companyProgress$,
      maxCompanyProgress$,
    ]).pipe(
      map(
        ([
          personProgress,
          maxPersonProgress,
          companyProgress,
          maxCompanyProgress,
        ]) => {
          const isPersonDone =
            (personProgress & maxPersonProgress) === maxPersonProgress;
          const isCompanyDone =
            (companyProgress & maxCompanyProgress) === maxCompanyProgress;
          return isPersonDone && isCompanyDone;
        }
      )
    );
  }
}
